/** external */
import _ from "lodash";
import update from "immutability-helper";

/** internal */
import {
  getAssessmentQuestionsFromCacheV2,
  writeAssessmentQuestionsInCacheV2,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import { updateActiveQuestion } from "Worksheet/modules/WorksheetModules";

/**
 * Updates the assessment questions cache with a new question, adjusting sequences as needed.
 * This function:
 * - Retrieves current questions from Apollo cache.
 * - Calculates the correct sequence for the new question.
 * - Shifts sequences of existing questions if inserting in between.
 * - Updates the cache with the new question list.
 * - Optionally switches to the new question.
 */
export const updateQuestionToCacheV2 = ({
  newQuestion,
  enableQuestionSwitch = true,
  questionId,
  parentItemId,
  questionOffset = 0,
  isAddingQuestionsInBetween = false,
  putAfterQuestionId,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    const assessmentQuestionsQueryVariables = {
      id: parentId,
      type: parentType,
      portalType: "PLANNER",
    };
    const cacheData = getAssessmentQuestionsFromCacheV2(
      assessmentQuestionsQueryVariables
    );

    const flatQuestions = _.get(cacheData, "questions.edges", []);
    // Create a map of questions by ID for fast lookup
    let questionsMap = _.keyBy(flatQuestions, "id");

    // Filter out questions that belong to the same parent (section)
    const questions = _.filter(
      flatQuestions,
      q => q.parentIdV2 === parentItemId
    );

    const totalQuestions = _.size(questions);

    // Calculate new sequence for the inserted question
    const newQuestionSequence = isAddingQuestionsInBetween
      ? _.get(questionsMap, [putAfterQuestionId, "sequence"], totalQuestions) +
        questionOffset +
        1
      : totalQuestions + 1; //sequence is 1-based(starts from 1)

    // Shift the sequence of existing questions that come after the new question's position
    _.forEach(questions, question => {
      const sequence = _.get(question, "sequence");
      if (sequence >= newQuestionSequence) {
        const { id } = question;
        questionsMap = update(questionsMap, {
          [id]: {
            sequence: { $set: sequence + 1 },
          },
        });
      }
    });

    // Set the sequence for the new question and add it to the map
    _.set(questionsMap, newQuestion.id, {
      ...newQuestion,
      parentIdV2: parentItemId,
      sequence: newQuestionSequence,
    });

    // Convert back to flat array to write to cache
    const updatedQuestions = _.values(questionsMap);

    const updatedCacheData = {
      node: update(cacheData, {
        questions: {
          edges: { $set: updatedQuestions },
        },
      }),
    };

    writeAssessmentQuestionsInCacheV2(
      assessmentQuestionsQueryVariables,
      updatedCacheData
    );

    if (enableQuestionSwitch && questionId) {
      dispatch(updateActiveQuestion(questionId));
    }
  };
};
